import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authApi } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loginWithOAuth: (provider: 'google' | 'github') => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  handleOAuthCallback: (provider: 'google' | 'github', code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await authApi.getProfile();
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('authToken');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [token]);

  const loginWithOAuth = async (provider: 'google' | 'github') => {
    try {
      let authUrl: string;
      if (provider === 'google') {
        authUrl = await authApi.getGoogleAuthUrl();
      } else {
        authUrl = await authApi.getGitHubAuthUrl();
      }

      // 重定向到 OAuth 提供商
      window.location.href = authUrl;
    } catch (error) {
      console.error(`${provider} OAuth error:`, error);
      throw error;
    }
  };

  const handleOAuthCallback = async (provider: 'google' | 'github', code: string) => {
    try {
      const response = await authApi.handleOAuthCallback(provider, code);
      const { user, token } = response.data;

      localStorage.setItem('authToken', token);
      setToken(token);
      setUser(user);
    } catch (error) {
      console.error('OAuth callback error:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loginWithOAuth,
    logout,
    isLoading,
    handleOAuthCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};